// Step 4 (WS-11) pure helpers — totals math, Confirm-Import payload assembly, and
// 422 pre-validation error parsing. No React, no API calls, no side effects, so the
// whole module is unit-testable in isolation.
//
// The request/response/error types below MIRROR the backend contract in
// CAP-Backend app/interfaces/smartImportInterface.ts. The FE does not import from
// the backend, so they are re-declared here; keep them in sync if the API changes.

import type { WizardState } from '../wizardReducer';
import { isTotalRow, parseNumber } from '../parsers';

// ---------------------------------------------------------------------------
// Backend contract mirror — POST /employee-cost/import/smart
// ---------------------------------------------------------------------------

export type FieldRef =
	| { type: 'existing'; fieldId: string }
	| { type: 'pending'; tempId: string };

export type EmployeeRef =
	| { type: 'existing'; employeeId: string }
	| { type: 'new'; rowId: string };

export interface SmartImportPendingField {
	tempId: string;
	label: string;
	sectionId: string;
	derivedJsonId: string;
}

export interface SmartImportNewEmployee {
	rowId: string;
	fullName: string;
	csvValue: string;
}

export interface SmartImportRowValue {
	fieldRef: FieldRef;
	value: string; // backend does parseFloat(value.trim())
	sourceHeader?: string;
}

export interface SmartImportRow {
	rowId: string;
	employeeRef: EmployeeRef;
	values: SmartImportRowValue[];
}

export interface SmartImportRequest {
	companyId: string;
	payPeriodId: string;
	pendingFields: SmartImportPendingField[];
	newEmployees: SmartImportNewEmployee[];
	reactivations: string[];
	rows: SmartImportRow[];
}

export interface SmartImportWarning {
	code: string;
	message: string;
	context?: unknown;
}

export interface SmartImportResponse {
	createdFields: number;
	createdEmployees: number;
	reactivatedEmployees: number;
	upsertedCosts: number;
	warnings: SmartImportWarning[];
}

export interface PreValidationError {
	code: string;
	message: string;
	rowId?: string;
	sourceHeader?: string;
	tempId?: string;
	employeeRef?: { type: 'existing' | 'new'; id?: string; rowId?: string };
	fieldRef?: { type: 'existing' | 'pending'; fieldId?: string; tempId?: string };
	context?: unknown;
}

// ---------------------------------------------------------------------------
// Totals (Functional Spec §6.2)
// ---------------------------------------------------------------------------

// Cost columns the import actually writes: a real section category with a chosen
// field. Same predicate the Step 3 aggregator (buildAggregatedRows) uses.
const isCostMapping = (m: WizardState['columnMappings'][number]): boolean =>
	m.category !== 'employee_name' &&
	m.category !== 'skip' &&
	m.fieldRef.type !== 'none';

/**
 * Two totals for the validation card:
 * - `importTotal` — sum of everything that will land in EmployeeCost. The
 *   aggregatedRows values are ALREADY post-negate and post-aggregation, so this is
 *   just their grand total (single source of truth; matches the preview grid).
 * - `fileTotal` — sum of the same source columns BEFORE any Convert +/- toggle,
 *   recomputed from rawMatrix (negate cannot be reversed out of the aggregate).
 * The only thing that can drive a mismatch is the Convert +/- flag — which is the
 * card's whole purpose.
 */
export function computeTotals(state: WizardState): {
	fileTotal: number;
	importTotal: number;
} {
	const importTotal = state.aggregatedRows.reduce(
		(sum, row) =>
			sum + Object.values(row.values).reduce((a, v) => a + v, 0),
		0
	);

	const costMappings = state.columnMappings.filter(isCostMapping);
	const dataRows = state.rawMatrix
		.slice(state.headerRowIndex + 1)
		.filter(
			(r) => r.some((c) => String(c ?? '').trim() !== '') && !isTotalRow(r)
		);

	let fileTotal = 0;
	for (const row of dataRows) {
		for (const m of costMappings) {
			const parsed = parseNumber(String(row[m.colIndex] ?? ''));
			if (!Number.isNaN(parsed)) fileTotal += parsed;
		}
	}

	return { fileTotal, importTotal };
}

// ---------------------------------------------------------------------------
// Confirm-Import payload assembly (Functional Spec §6.4, Tech Spec §4.1)
// ---------------------------------------------------------------------------

// A field key in aggregatedRows.values / columnMappings is a pending tempId when it
// carries one of the two pending prefixes: `__pending__N` (inline "+ Add New Field")
// or `__template_pending__<colIndex>` (a template-reconstructed field — see
// step2Helpers.resolveTemplateToMappings). Real field ids are cuids and never
// contain this substring.
export const isPendingFieldKey = (key: string): boolean =>
	key.startsWith('__pending__') || key.startsWith('__template_pending__');

const fieldRefForKey = (key: string): FieldRef =>
	isPendingFieldKey(key)
		? { type: 'pending', tempId: key }
		: { type: 'existing', fieldId: key };

/**
 * Assemble the full SmartImportRequest from wizard state. `aggregatedRows` and
 * `employeeRows` are joined by their shared `rowId` (employeeRows were computed
 * from aggregatedRows in Step 3, preserving the id).
 */
export function buildConfirmPayload(
	state: WizardState,
	ctx: { companyId: string; payPeriodId: string }
): SmartImportRequest {
	const employeeByRowId = new Map(
		state.employeeRows.map((r) => [r.rowId, r])
	);

	// ENG-414: rows the user marked Skip in Step 3 are excluded from the import
	// entirely. Every payload array below derives from `importableRows` (or is
	// filtered by `isSkipped`) so a skip can't leak in through any one of them —
	// notably `newEmployees` and `reactivations`, which read employeeRows directly
	// and would otherwise still create/reactivate an employee for a skipped row.
	const importableRows = state.employeeRows.filter((r) => !r.skipped);
	const isSkipped = (rowId: string): boolean =>
		employeeByRowId.get(rowId)?.skipped === true;

	const newEmployees: SmartImportNewEmployee[] = importableRows
		.filter((r) => r.matchStatus === 'new')
		.map((r) => ({
			rowId: r.rowId,
			// ENG-421: prefer the normalized "Last, First" name (computed in Step 3)
			// so multi-column layouts — which assemble a comma-less "First Last"
			// (e.g. "Cosmo Kramer") — don't get rejected by the backend's
			// employee_name_invalid guard. addAsNewName covers a name the user typed
			// in Step 3; csvName is the last resort.
			fullName: r.normalizedName ?? r.addAsNewName ?? r.csvName,
			csvValue: r.csvName,
		}));

	// Auto-reactivation (ENG-370): an inactive matched employee is reactivated on
	// confirm iff the import actually brings values for them. "Has values" is
	// checked against the aggregated row that will be sent — at least one field
	// value that isn't zero/empty (aggregatedRows values are already coerced
	// numbers, so a NaN/blank cell reads as 0 here and a 0 means nothing to import).
	const aggregatedByRowId = new Map(
		state.aggregatedRows.map((r) => [r.rowId, r])
	);
	const rowHasValues = (rowId: string): boolean => {
		const agg = aggregatedByRowId.get(rowId);
		return agg ? Object.values(agg.values).some((v) => v !== 0) : false;
	};

	const reactivations: string[] = importableRows
		.filter(
			(r) => r.inactive && r.matchedEmployeeId && rowHasValues(r.rowId)
		)
		.map((r) => r.matchedEmployeeId as string);

	const rows: SmartImportRow[] = state.aggregatedRows
		.filter((row) => !isSkipped(row.rowId))
		.map((row) => {
			const twin = employeeByRowId.get(row.rowId);
			const employeeRef: EmployeeRef =
				twin && twin.matchStatus === 'new'
					? { type: 'new', rowId: row.rowId }
					: {
							type: 'existing',
							employeeId: (twin?.matchedEmployeeId ?? '') as string,
					  };
			return {
				rowId: row.rowId,
				employeeRef,
				// Send all values as-is, including zeros (backend upsert is additive).
				values: Object.entries(row.values).map(([key, num]) => ({
					fieldRef: fieldRefForKey(key),
					value: String(num),
				})),
			};
		});

	return {
		companyId: ctx.companyId,
		payPeriodId: ctx.payPeriodId,
		// Send all pending fields; the user added them deliberately and the backend
		// dedups/creates them.
		pendingFields: state.pendingFields.map((pf) => ({
			tempId: pf.tempId,
			label: pf.label,
			sectionId: pf.sectionId,
			derivedJsonId: pf.derivedJsonId,
		})),
		newEmployees,
		reactivations,
		rows,
	};
}

// ---------------------------------------------------------------------------
// 422 pre-validation error parsing + display helpers
// ---------------------------------------------------------------------------

/**
 * Extract the PreValidationError[] from a 422 axios error. The backend nests the
 * structured payload as a JSON STRING under response.data.message (errorHandler
 * passes CustomError.message through for non-500s). Returns [] for any other error.
 */
export function parseConfirmErrors(err: unknown): PreValidationError[] {
	const raw = (err as { response?: { data?: { message?: unknown } } })?.response
		?.data?.message;
	if (typeof raw !== 'string') return [];
	try {
		const parsed = JSON.parse(raw);
		return Array.isArray(parsed?.errors) ? parsed.errors : [];
	} catch {
		return [];
	}
}

/** Human-readable locus label shown beside an error message in the error panel. */
export function getErrorLocus(
	err: PreValidationError,
	pendingFields: WizardState['pendingFields'] = [],
	employeeRows: WizardState['employeeRows'] = []
): string {
	if (err.sourceHeader) return `Column: ${err.sourceHeader}`;
	const tempId = err.tempId ?? err.fieldRef?.tempId;
	if (tempId) {
		const label = pendingFields.find((pf) => pf.tempId === tempId)?.label;
		return label ? `Field: ${label}` : `Field: ${tempId}`;
	}
	if (err.employeeRef) {
		const rowId = err.employeeRef.rowId ?? err.employeeRef.id;
		const csvName = rowId
			? employeeRows.find((r) => r.rowId === rowId)?.csvName
			: undefined;
		return csvName ? `Employee: ${csvName}` : `Employee (row ?)`;
	}
	if (err.rowId) {
		// rowIds are UUIDs — resolve to the source employee name where possible.
		const csvName = employeeRows.find((r) => r.rowId === err.rowId)?.csvName;
		return csvName ? `Employee: ${csvName}` : `Row: ${err.rowId}`;
	}
	return '';
}

/**
 * Which earlier step's "Fix" link to surface (zero-indexed currentStep target):
 * - 1 (Step 2 — Map Columns) for column / field / pending-field errors.
 * - 2 (Step 3 — Map Employees) for employee / reactivation errors.
 * - null when no navigation target is meaningful.
 */
export function getErrorStep(err: PreValidationError): 1 | 2 | null {
	if (err.sourceHeader || err.tempId || err.fieldRef) return 1;
	if (err.employeeRef) return 2;
	return null;
}

// ---------------------------------------------------------------------------
// Currency formatting (no shared util exists in utils/utils.tsx)
// ---------------------------------------------------------------------------

const usd = new Intl.NumberFormat('en-US', {
	style: 'currency',
	currency: 'USD',
});

/** Format a number as `$X,XXX.XX`. */
export function formatCurrency(n: number): string {
	return usd.format(Number.isFinite(n) ? n : 0);
}
